import type { LoggerAdapter } from '@signage/player-core';

interface WatchdogOptions {
  logger: LoggerAdapter;
  /** False when the playback surface has stopped reporting itself alive. */
  isHealthy: () => boolean;
  onUnhealthy: () => Promise<void>;
  intervalMs?: number;
}

/**
 * In-process watchdog.
 *
 * The playback surface pings the bridge every few seconds. If those pings stop while the app is
 * still foreground, playback has wedged — a decode failure, a stuck video element, a script
 * error in a timer — and the screen is showing a frozen frame that looks fine from a distance
 * but is no longer following its schedule. Reloading the page rebuilds the surface from the
 * cached manifest, which is far cheaper than restarting the process.
 *
 * Crashes of the process itself are recovered outside this: Android restarts the activity, and
 * `BootReceiver` brings the player back after a power cut.
 */
export function startWatchdog(options: WatchdogOptions): () => void {
  const interval = options.intervalMs ?? 10_000;
  let recovering = false;

  const timer = setInterval(() => {
    // Backgrounded apps get their timers throttled, so an unhealthy reading there means nothing.
    if (document.visibilityState !== 'visible') return;
    if (recovering || options.isHealthy()) return;

    recovering = true;
    void options
      .onUnhealthy()
      .catch((err: unknown) => {
        options.logger.error('Watchdog recovery failed', { error: (err as Error).message });
      })
      .finally(() => {
        recovering = false;
      });
  }, interval);

  return () => clearInterval(timer);
}
